import '@satellite/polyfills';
import '@satellite/locales/localeImports.js';

//VUE
import { createApp, defineAsyncComponent } from 'vue';
import { plugin as featureFlagPlugin } from '@loadsmart/vue-feature-flags';
import { createPinia } from 'pinia';

// APP
import App from './App.vue';
import config from './config/config';
import { initStore } from './store';

// Mixins
import localStorageMixin from '@satellite/components/mixins/localStorageMixin';
import mappedGetters from '@/components/mixins/mappedGetters';
import mappedStates from '@/components/mixins/mappedStates';
import utilityMixin from '@/components/mixins/utilityMixin';

// Composables
import { HTTP_CLIENT_KEY } from '@satellite/composables/useHttpClient';

// Plugins
import AxiosPlugin from '@satellite/plugins/AxiosPlugin';
import VueTelInput from 'vue3-tel-input';
import 'vue3-tel-input/dist/vue3-tel-input.css';
import ConfirmDialogPlugin from '@satellite/plugins/ConfirmDialog/ConfirmDialogPlugin';
import SocketPlugin from '@satellite/plugins/SocketPlugin';
import util from '@satellite/plugins/util';
import validation from '@satellite/plugins/Validator';
import initSentry from '@satellite/plugins/sentry';
import initDatadogRum from './plugins/datadogRumPlugin';
import globalStoreAccessorsPlugin from './plugins/globalStoreAccessorsPlugin';
import VueQueryPluginInitializer from '@satellite/plugins/VueQueryPlugin';
import vuetify from './plugins/vuetifyPlugin';
import Notifications from '@kyvg/vue3-notification';
import velocity from 'velocity-animate';
import featureFlagConfig from './plugins/featureFlagPlugin';
import initializeRouter from '@/routes.js';
import i18n from './plugins/i18n';
import { setTranslator as setCoreTranslator, uiAppName } from '@nova/core';
import { setTranslator } from '@satellite/plugins/translator';

// Directives
import { registerSearchHighlightDirective } from '@satellite/directives/searchHighlight';

// Deps
import '@mdi/font/css/materialdesignicons.css'; // Ensure you are using css-loader

import warehouseService from '@satellite/services/warehouse-service';
import userService from '@satellite/services/user-service';
import dockService from '@satellite/services/dock-service';
import appointmentService from '@satellite/services/appointment-service';
import warehouseGroupService from '@satellite/services/warehouse-group-service';
import companyService from '@satellite/services/company-service';
import unitLimitService from '@satellite/services/unit-limits-service';
import reportingService from '@satellite/services/reporting-service';
import metricsService from '@satellite/services/metrics-service';
import { analyticsKey } from '@satellite/composables/useAnalytics';
import { createHeliosAnalyticsClient } from '@/analytics/client';
import { useHeliosAnalytics } from '@/composables/useHeliosAnalytics';
import { disableAnimationsAndTransitions } from './utils.js';

import { useEventHub } from '@/composables/index.js';
import { Settings } from 'luxon';

// Set default locale so working with Weekdays does not break for different locales
Settings.defaultLocale = 'en-US';

const app = createApp(App);

registerSearchHighlightDirective(app);

const pinia = createPinia();
app.use(pinia);

// Init Plugins
AxiosPlugin.init('/login', uiAppName.HELIOS, app);
app.provide(HTTP_CLIENT_KEY, window.axios);
VueQueryPluginInitializer.init(app);

// MODULES
// app.use(VueMask);
/*
 * Register <apexchart> as an async global component so apexcharts (~570 KB) stays out
 * of the eager boot bundle. It is only rendered by chart components on the lazy-loaded
 * Insights routes, so it now loads on demand the first time a chart renders.
 */
app.component(
  'apexchart',
  defineAsyncComponent({
    loader: () => import('vue3-apexcharts'),
    delay: 200,
    timeout: 10000
  })
);
app.use(Notifications, { velocity });
app.use(featureFlagPlugin, { config: featureFlagConfig });

// Vue Global Properties
app.config.globalProperties.util = util;
app.config.globalProperties.$validator = validation;
app.config.globalProperties.config = config;
app.config.globalProperties.$globalLoading = false;
app.config.globalProperties.notify = utilityMixin.methods.notify;
app.config.globalProperties.getNotificationTitle = utilityMixin.methods.getNotificationTitle;
app.config.globalProperties.setDirtyStatus = utilityMixin.methods.setDirtyStatus;
app.config.globalProperties.checkDirtyConfirmation = utilityMixin.methods.checkDirtyConfirmation;

// Services
app.config.globalProperties.services = {
  warehouse: warehouseService,
  warehouseGroup: warehouseGroupService,
  user: userService,
  dock: dockService,
  appointment: appointmentService,
  company: companyService,
  unitLimit: unitLimitService,
  reporting: reportingService,
  metrics: metricsService
};

// Vue Global Mixins
app.mixin(utilityMixin);
app.mixin(localStorageMixin);

ConfirmDialogPlugin.init(app);

disableAnimationsAndTransitions();

initStore().then(async store => {
  app.use(store);
  app.use(globalStoreAccessorsPlugin, { store, mixins: [mappedStates, mappedGetters] });

  // Helios opts out of the self-made event check: the yard experience reconciles state through
  // TanStack Query and needs every Subspace event, including the echo of the user's own action.
  app.config.globalProperties.sockets = new SocketPlugin(app, { skipSelfMadeEventCheck: true });
  store.$app = app;

  const analyticsClient = createHeliosAnalyticsClient();
  app.provide(analyticsKey, analyticsClient);
  app.config.globalProperties.analytics = analyticsClient;
  app.runWithContext(() => useHeliosAnalytics());

  /**
   * Create a bound notify function for the store, e.g. `store.notify('message', 'success')`.
   */
  store.notify = (message, type = 'success', options = {}) => {
    app.config.globalProperties.notify(message, type, options);
  };

  app.use(VueTelInput);

  const routes = await initializeRouter(store, analyticsClient);
  app.use(routes);

  const eventHub = useEventHub();
  app.config.globalProperties.$eventHub = eventHub;

  initSentry(app, routes, config);

  // Initialize Datadog RUM
  initDatadogRum(config);

  // Import and use vuetify
  app.use(vuetify);
  app.use(i18n);

  setTranslator(i18n.global.t, i18n.global.locale);
  setCoreTranslator((key, params) => i18n.global.t(key, params));

  // Call again after Vuetify is initialized to ensure styles override Vuetify's
  disableAnimationsAndTransitions();

  app.mount('#app');

  // Call one last time after the app is mounted
  setTimeout(disableAnimationsAndTransitions, 100);
});

/**
 * HACK: this is necessary to access the app instance outside of vue context.
 * For example, in order to use the app context to access unleash flags on the
 * stores we need to use the `app.runWithContext` method:
 * ```
   const enabled = window.app?.runWithContext(() => useClient()?.isEnabled?.('feature-flag-name'));
 * ```
 * @see https://vuejs.org/api/application#app-runwithcontext
 */
window.app = app;

export default app;
